/**
 * Page 2 of Form 2848 (Rev. January 2021), replicated.
 *
 * Coordinates and per-run `fit` widths are measured out of the official PDF;
 * rules and checkboxes come from f2848-rules.ts. See F2848Page1 for the
 * layout approach.
 */
import { At, Arrow, Cb, Field, Leader, Rules, pt } from "./f2848-layout";
import { H_SOLID_P2, H_DASHED_P2, V_RULES_P2 } from "./f2848-rules";

/** The official's bullet is heavier than Arial's U+2022 at the same size. */
const Bullet = () => (
  <span style={{ fontSize: "1.3em", verticalAlign: "-0.1em" }}>•</span>
);

interface PageProps {
  value?: any;
  onChange?: (patch: any) => void;
  disabled?: boolean;
}

/** The a–r representative designations, with their measured widths. */
const DESIGNATIONS: Array<[number, string, number, React.ReactNode]> = [
  [
    362,
    "a",
    360.4,
    "Attorney—a member in good standing of the bar of the highest court of the jurisdiction shown below.",
  ],
  [
    374,
    "b",
    475.2,
    "Certified Public Accountant—a holder of an active license to practice as a certified public accountant in the jurisdiction shown below.",
  ],
  [
    386,
    "c",
    305.8,
    "Enrolled Agent—enrolled as an agent by the IRS per the requirements of Circular 230.",
  ],
  [398, "d", 200.6, "Officer—a bona fide officer of the taxpayer organization."],
  [
    410,
    "e",
    209.4,
    "Full-Time Employee—a full-time employee of the taxpayer.",
  ],
  [
    422,
    "f",
    523.5,
    "Family Member—a member of the taxpayer’s immediate family (spouse, parent, child, grandparent, grandchild, step-parent, step-child, brother, or sister).",
  ],
];

const F2848Page2 = ({ value, onChange, disabled }: PageProps) => {
  const v = value || {};
  const set =
    (name: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
      onChange?.({ [name]: e.target.value });

  return (
  <>
    <Rules
      h={H_SOLID_P2}
      v={V_RULES_P2}
      dashed={H_DASHED_P2}
      /* 0.75pt above Part II's heading, 1pt on the footer rule */
      hWeights={{ 288: 0.75, 720: 1 }}
    />

    {/* running head */}
    <At x={36} y={37} size={7} fit={77.5}>Form 2848 (Rev. 1-2021)</At>
    <At x={552} y={37} size={7} fit={16.5}>Page</At>
    <At x={570} y={34} size={10} bold leading={10}>
      2
    </At>

    {/* =============================== 5b =============================== */}
    <At x={50} y={51} size={8} bold>b</At>
    <At x={65} y={51} size={8} bold fit={108.7}>Specific acts not authorized.</At>
    <At x={176} y={51} size={8} fit={391.9}>
      My representative(s) is (are) not authorized to endorse or otherwise
      negotiate any check (including directing or
    </At>
    <At x={65} y={62} size={8} fit={496.0}>
      accepting payment by any means, electronic or otherwise, into an account
      owned or controlled by the representative(s) or any firm or other
    </At>
    <At x={65} y={73} size={8} fit={420.7}>
      entity with whom the representative(s) is (are) associated) issued by the
      government in respect of a federal tax liability.
    </At>
    <At x={65} y={85} size={8} fit={421.0}>
      List any other specific deletions to the acts otherwise authorized in this
      power of attorney (see instructions for line 5b):
    </At>
    <Field x={492} y={84} w={82} h={11} disabled />
    <Field x={65} y={96} w={509} h={11} disabled />

    {/* =============================== 6 ================================ */}
    <At x={46} y={111} size={8} bold>6</At>
    <At x={65} y={111} size={8} bold fit={198.5}>
      Retention/revocation of prior power(s) of attorney.
    </At>
    <At x={267} y={111} size={8} fit={294.6}>
      The filing of this power of attorney automatically revokes all earlier
      power(s) of
    </At>
    <At x={65} y={122} size={8} fit={438.6}>
      attorney on file with the Internal Revenue Service for the same matters
      and years or periods covered by this form. If you
    </At>
    <At x={506} y={122} size={8} bold fit={25.5}>do not</At>
    <At x={534.32} y={122} size={8} fit={27.3}>want to</At>
    <At x={65} y={133} size={8}>
      revoke a prior power of attorney, check here <Leader n={26} />
    </At>
    <Arrow x={554} y={135} size={5} />
    <Cb x={568} y={133} />
    <At x={64.8} y={143.5} size={9} bold fit={419.7}>
      YOU MUST ATTACH A COPY OF ANY POWER OF ATTORNEY YOU WANT TO REMAIN IN
      EFFECT.
    </At>

    {/* =============================== 7 ================================ */}
    <At x={46} y={160} size={8} bold>7</At>
    <At x={65} y={160} size={8} bold fit={137.3}>
      Taxpayer declaration and signature.
    </At>
    <At x={205} y={160} size={8} fit={371.0}>
      If a tax matter concerns a year in which a joint return was filed, each
      spouse must file a separate power
    </At>
    <At x={65} y={171} size={8} fit={511.1}>
      of attorney even if they are appointing the same representative(s). If
      signed by a corporate officer, partner, guardian, tax matters partner,
    </At>
    <At x={65} y={182} size={8} fit={511.1}>
      partnership representative (or designated individual, if applicable),
      executor, receiver, administrator, trustee, or individual other than the
    </At>
    <At x={65} y={193} size={8} fit={318.0}>
      taxpayer, I certify I have the legal authority to execute this form on
      behalf of the taxpayer.
    </At>
    <Arrow x={65} y={207} size={5} />
    <At x={74.3} y={205} size={9} bold fit={496.1}>
      IF NOT COMPLETED, SIGNED, AND DATED, THE IRS WILL RETURN THIS POWER OF
      ATTORNEY TO THE TAXPAYER.
    </At>

    {/* signature block — the rules themselves are in H_DASHED_P2 */}
    <Field x={36} y={228} w={215} h={11} disabled />
    <At x={130} y={241} size={8} fit={34.7}>Signature</At>
    <Field x={266} y={228} w={70} h={11} disabled />
    <At x={301} y={241} size={8} fit={17.0}>Date</At>
    <Field x={351} y={228} w={223} h={11} disabled />
    <At x={436} y={241} size={8} fit={64.2}>Title (if applicable)</At>

    <Field x={36} y={264} w={215} h={11} disabled />
    <At x={128} y={277} size={8} fit={38.9}>Print name</At>
    <Field x={266} y={264} w={308} h={11} disabled />
    <At x={324} y={277} size={8} fit={201.8}>
      Print name of taxpayer from line 1 if other than individual
    </At>

    {/* ============================= Part II ============================ */}
    <div
      style={{
        position: "absolute",
        left: pt(36),
        top: pt(287.75),
        width: pt(35.75),
        height: pt(12.5),
        background: "#000",
      }}
    />
    <At x={40} y={287} size={10} bold fit={28.2} style={{ color: "#fff" }}>
      Part II
    </At>
    <At x={86} y={287} size={10} bold fit={142.0}>
      Declaration of Representative
    </At>

    <At x={38.22} y={302} size={8} fit={228.6}>
      Under penalties of perjury, by my signature below I declare that:
    </At>
    <At x={36} y={314} size={8}>
      <Bullet /> I am not currently suspended or disbarred from practice, or ineligible
      for practice, before the Internal Revenue Service;
    </At>
    <At x={36} y={326} size={8}>
      <Bullet /> I am subject to regulations in Circular 230 (31 CFR, Subtitle A, Part
      10), as amended, governing practice before the Internal Revenue Service;
    </At>
    <At x={36} y={338} size={8}>
      <Bullet /> I am authorized to represent the taxpayer identified in Part I for the
      matter(s) specified there; and
    </At>
    <At x={36} y={350} size={8}><Bullet /> I am one of the following:</At>

    {DESIGNATIONS.map(([y, letter, w, body]) => (
      <div key={letter}>
        <At x={44} y={y} size={8} bold>{letter}</At>
        <At x={53} y={y} size={8} fit={w}>{body}</At>
      </div>
    ))}

    <At x={44} y={434} size={8} bold>g</At>
    <At x={53} y={434} size={8} fit={519.1}>
      Enrolled Actuary—enrolled as an actuary by the Joint Board for the
      Enrollment of Actuaries under 29 U.S.C. 1242 (the authority to practice
      before
    </At>
    <At x={53} y={444} size={8} fit={184.9}>
      the IRS is limited by section 10.3(d) of Circular 230).
    </At>

    <At x={44} y={456} size={8} bold>h</At>
    <At x={53} y={456} size={8} fit={522.3}>
      Unenrolled Return Preparer—Authority to practice before the IRS is
      limited. An unenrolled return preparer may represent, provided the
      preparer (1)
    </At>
    <At x={53} y={466} size={8} fit={505.1}>
      prepared and signed the return or claim for refund (or prepared if there
      is no signature space on the form); (2) was eligible to sign the return or
    </At>
    <At x={53} y={475} size={8} fit={444.9}>
      claim for refund; (3) has a valid PTIN; and (4) possesses the required
      Annual Filing Season Program Record of Completion(s).
    </At>
    <At x={500} y={475} size={8} italic fit={14.5}>See</At>
    <At x={516.76} y={475} size={8} bold fit={51.7}>Special Rules</At>
    <At x={53} y={485} size={8} bold fit={194.5}>
      and Requirements for Unenrolled Return Preparers
    </At>
    <At x={249.73} y={485} size={8} bold italic fit={170.7}>
      in the instructions for additional information.
    </At>

    <At x={44} y={497} size={8} bold>k</At>
    <At x={53} y={497} size={8} fit={515.2}>
      Qualifying Student or Law Graduate—receives permission to represent
      taxpayers before the IRS by virtue of his/her status as a law, business,
      or
    </At>
    <At x={53} y={507} size={8} fit={481.8}>
      accounting student, or law graduate working in a LITC or STCP. See
      instructions for Part II for additional information and requirements.
    </At>

    <At x={44} y={519} size={8} bold>r</At>
    <At x={53} y={519} size={8} fit={512.4}>
      Enrolled Retirement Plan Agent—enrolled as a retirement plan agent under
      the requirements of Circular 230 (the authority to practice before the
    </At>
    <At x={53} y={529} size={8} fit={192.3}>
      Internal Revenue Service is limited by section 10.3(e)).
    </At>

    <Arrow x={53} y={544} size={5} />
    <At x={62.63} y={542} size={9} bold fit={513.4}>
      IF THIS DECLARATION OF REPRESENTATIVE IS NOT COMPLETED, SIGNED, AND DATED,
      THE IRS WILL RETURN THE
    </At>
    <At x={53.03} y={553} size={9} bold fit={425.5}>
      POWER OF ATTORNEY. REPRESENTATIVES MUST SIGN IN THE ORDER LISTED IN PART
      I, LINE 2.
    </At>

    <At x={36} y={565} size={8} bold fit={20.5}>Note:</At>
    <At x={59} y={565} size={8} fit={409.4}>
      For designations d–f, enter your title, position, or relationship to the
      taxpayer in the “Licensing jurisdiction” column.
    </At>

    {/* Part II table headings */}
    <At x={43} y={585} size={8} fit={50.6}>Designation—</At>
    <At x={46} y={595} size={8} fit={44.5}>Insert above</At>
    <At x={49} y={604} size={8} fit={18.0}>letter</At>
    <At x={69} y={604} size={8} bold fit={18.9}>(a–r).</At>

    <At x={103} y={581} size={8} fit={75.0}>Licensing jurisdiction</At>
    <At x={114} y={590} size={8} fit={53.0}>(State) or other</At>
    <At x={108} y={600} size={8} fit={64.9}>licensing authority</At>
    <At x={117} y={609} size={8} fit={47}>(if applicable)</At>

    <At x={193} y={585} size={8} fit={89.6}>Bar, license, certification,</At>
    <At x={192} y={594} size={8} fit={91.4}>registration, or enrollment</At>
    <At x={199} y={604} size={8} fit={77}>number (if applicable)</At>

    <At x={390} y={594} size={8} fit={33.9}>Signature</At>
    <At x={539} y={594} size={8} fit={16.6}>Date</At>

    {/* ============================= footer ============================= */}
    <At x={492} y={723} size={7} fit={16.1}>Form</At>
    <At x={510} y={720} size={10} bold fit={22.3} leading={10}>
      2848
    </At>
    <At x={533.5} y={723} size={7} fit={42.5}>(Rev. 1-2021)</At>

    {/* Declaration of Representative, first row. The previous build
        printed these three as fixed values from DeclationTable; the
        columns are split by V_RULES_P2 at 100.5 / 179.8 / 295.0 and the
        first data row runs between the rules at y 624 and 648. */}
    <Field x={38} y={630} w={60} h={12} style={{ textAlign: "center" }}
      value={v?.repDesignation ?? "C"} onChange={set("repDesignation")} disabled={disabled} />
    <Field x={102.5} y={630} w={75} h={12} style={{ textAlign: "center" }}
      value={v?.repJurisdiction ?? "IRS"} onChange={set("repJurisdiction")} disabled={disabled} />
    <Field x={181.8} y={630} w={111} h={12} style={{ textAlign: "center" }}
      value={v?.repLicense ?? "00144051-EA"} onChange={set("repLicense")} disabled={disabled} />
  </>
  );
};

export default F2848Page2;
